import { useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Dialog } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { api } from "@/services/api"
import { authService } from "@/services/authService"
import { useAuthStore } from "@/store/authStore"

const apiError = (e: unknown) => (e as { response?: { data?: { error?: string } } })?.response?.data?.error

/**
 * Pede o aceite da versão vigente dos Termos e da Política (ADR-010) a quem tem conta e ainda não aceitou.
 * Não fecha sem decisão. Quem não concorda pode sair ou ir às Configurações, onde o aviso não aparece, para
 * baixar os dados e excluir a conta: recusar os termos não pode bloquear os direitos do titular.
 */
export function TermsUpdateDialog() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const user = useAuthStore((s) => s.user)
  const setAuth = useAuthStore((s) => s.setAuth)
  const logout = useAuthStore((s) => s.logout)
  const [saving, setSaving] = useState(false)

  const version = user?.pendingTermsVersion
  if (!user || !version || pathname === "/settings") return null

  const accept = async () => {
    setSaving(true)
    try {
      await api.post("/account/terms", { version })
      setAuth({ ...user, pendingTermsVersion: null })
    } catch (e) {
      toast.error(apiError(e) ?? "Não foi possível registrar o aceite. Tente de novo.")
    } finally {
      setSaving(false)
    }
  }

  const leave = async () => {
    await authService.logout()
    logout()
    navigate("/login")
  }

  return (
    <Dialog
      open
      onClose={() => {}}
      dismissible={false}
      busy={saving}
      title="Atualizamos os Termos de Uso e a Política de Privacidade"
      description="Para continuar usando o FinControl, leia e aceite a versão atual."
      footer={
        <>
          <Button variant="outline" onClick={leave} disabled={saving}>Sair da conta</Button>
          <Button onClick={accept} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Li e aceito
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-[14px] leading-relaxed">
        <p>
          Os documentos explicam quais dados guardamos, com quem compartilhamos, por quanto tempo e como exportar
          ou apagar tudo.
        </p>
        <ul className="space-y-1.5">
          <li><Link to="/privacidade" target="_blank" className="font-medium underline underline-offset-2">Política de Privacidade</Link></li>
          <li><Link to="/termos" target="_blank" className="font-medium underline underline-offset-2">Termos de Uso</Link></li>
        </ul>
        <p className="text-muted-foreground text-[13px]">
          Se não concordar, você pode{" "}
          <Link to="/settings?tab=account" className="underline underline-offset-2">baixar seus dados e excluir a conta</Link>.
        </p>
      </div>
    </Dialog>
  )
}
